import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import api, { formatApiError } from "../../lib/api";
import { formatDate as fmt } from "../../lib/date";
import { Lightbulb, AlertTriangle, Loader2, Send } from "lucide-react";

const STATUS_BADGE = {
  open: "bg-amber-100 text-amber-700",
  reviewed: "bg-blue-100 text-blue-700",
  actioned: "bg-green-100 text-green-700",
  closed: "bg-slate-100 text-slate-600",
};

function SuggestionBox() {
  const [category, setCategory] = useState("suggestion");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  const load = () => api.get("/me/suggestions").then(({ data }) => setMine(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!text.trim()) { toast.error("Please enter your suggestion."); return; }
    setBusy(true);
    try {
      await api.post("/me/suggestions", { category, text });
      toast.success("Thanks! Your suggestion has been sent to the committee.");
      setText(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="suggestion-box">
      <div className="flex items-center gap-2 text-[#0F2744] mb-1"><Lightbulb className="h-5 w-5 text-amber-500" /><h2 className="font-bold text-lg">Suggestion box</h2></div>
      <p className="text-sm text-slate-500 mb-4">Have an idea to improve the club, or a ride / event you'd love us to run? Let the committee know.</p>
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-400 mb-1 block">Type</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="suggestion-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="suggestion">General suggestion</SelectItem>
              <SelectItem value="event_idea">Event / ride idea</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Share your idea…" data-testid="suggestion-text" />
        <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold" data-testid="suggestion-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Send suggestion</>}
        </Button>
      </div>
      {mine.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100 space-y-2">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Your previous suggestions</p>
          {mine.map((s) => (
            <div key={s.id} className="flex items-start justify-between gap-3 text-sm" data-testid={`my-suggestion-${s.id}`}>
              <span className="text-slate-700">{s.text}</span>
              <Badge className={`${STATUS_BADGE[s.status] || STATUS_BADGE.open} shrink-0 capitalize`}>{s.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IncidentReport() {
  const [form, setForm] = useState({ date: "", location: "", description: "", injuries: "", vehicles: "" });
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/me/incidents").then(({ data }) => setMine(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.description.trim()) { toast.error("Please describe what happened."); return; }
    setBusy(true);
    try {
      await api.post("/me/incidents", form);
      toast.success("Thank you — your incident report has been sent to the committee.");
      setForm({ date: "", location: "", description: "", injuries: "", vehicles: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="incident-report">
      <div className="flex items-center gap-2 text-[#0F2744] mb-1"><AlertTriangle className="h-5 w-5 text-red-500" /><h2 className="font-bold text-lg">Report an incident</h2></div>
      <p className="text-sm text-slate-500 mb-4">Report an accident or near-miss so the committee can review it and share lessons learned with the group.</p>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-slate-400 mb-1 block">Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="incident-date" /></div>
          <div><Label className="text-xs text-slate-400 mb-1 block">Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Where did it happen?" data-testid="incident-location" /></div>
        </div>
        <div><Label className="text-xs text-slate-400 mb-1 block">What happened?</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="Describe the incident…" data-testid="incident-description" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-slate-400 mb-1 block">Any injuries?</Label><Input value={form.injuries} onChange={(e) => set("injuries", e.target.value)} placeholder="Optional" data-testid="incident-injuries" /></div>
          <div><Label className="text-xs text-slate-400 mb-1 block">Vehicles involved</Label><Input value={form.vehicles} onChange={(e) => set("vehicles", e.target.value)} placeholder="Optional" data-testid="incident-vehicles" /></div>
        </div>
        <Button onClick={submit} disabled={busy} className="bg-[#0F2744] hover:bg-[#0B192C] text-white font-semibold" data-testid="incident-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Submit report</>}
        </Button>
      </div>
      {mine.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100 space-y-2">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Your previous reports</p>
          {mine.map((s) => (
            <div key={s.id} className="flex items-start justify-between gap-3 text-sm" data-testid={`my-incident-${s.id}`}>
              <span className="text-slate-700">{s.date ? `${fmt(s.date)} — ` : ""}{s.description.slice(0, 80)}{s.description.length > 80 ? "…" : ""}</span>
              <Badge className={`${STATUS_BADGE[s.status] || STATUS_BADGE.open} shrink-0 capitalize`}>{s.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

import { Link } from "react-router-dom";

export default function MemberExtras() {
  return (
    <div className="mt-8 space-y-6" data-testid="member-extras">
      <div className="grid sm:grid-cols-2 gap-4">
        <a href="#member-feedback" className="rounded-2xl bg-[#0F2744] text-white p-5 flex items-center gap-3 hover:bg-[#0B192C] transition-colors" data-testid="tile-feedback">
          <Lightbulb className="h-6 w-6 text-amber-400 shrink-0" />
          <div><div className="font-bold">Feedback</div><div className="text-sm text-slate-300">Suggest ideas or report an incident</div></div>
        </a>
        <Link to="/profile" className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-3 hover:border-[#0F2744] transition-colors" data-testid="tile-gdpr">
          <ShieldCheckIcon />
          <div><div className="font-bold text-[#0F2744]">Your data &amp; privacy</div><div className="text-sm text-slate-500">See or request your data, or request deletion</div></div>
        </Link>
      </div>
      <div id="member-feedback" className="grid md:grid-cols-2 gap-6">
        <SuggestionBox />
        <IncidentReport />
      </div>
    </div>
  );
}

function ShieldCheckIcon() {
  return <svg className="h-6 w-6 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>;
}
